import { useState } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedFetch } from '@/utils/authenticatedFetch';

interface Expense {
  id: string;
  title: string;
  description: string;
  amount: number;
  currency: string;
  categoryId: string;
  vendorName?: string;
  expenseDate: string;
  paymentMethod: string;
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'paid' | 'reimbursed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  receiptFiles: any[];
  tags: string[];
  createdAt: string;
  createdBy: string;
}

interface ExpenseSummary {
  totalExpenses: number;
  totalTransactions: number;
  expensesByStatus: Record<string, number>;
  expensesByCategory: Record<string, number>;
  pendingApproval: number;
  awaitingPayment: number;
  fromDate: string;
  toDate: string;
}

interface ExpenseCategory {
  id: string;
  name: string;
  description?: string;
  code: string;
  requiresApproval: boolean;
  approvalThreshold?: number;
  isActive: boolean;
}

export default function ExpenseProofPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const queryClient = useQueryClient();

  const { data: expenses = [] as Expense[], isLoading: expensesLoading, error: expensesError } = useQuery<Expense[]>({
    queryKey: ['expenses', { status: selectedStatus, search: searchTerm }],
    queryFn: async () => {
      const params = new URLSearchParams({
        ...(selectedStatus && selectedStatus !== 'all' && { status: selectedStatus }),
        ...(searchTerm && { search: searchTerm }),
      });
      
      const response = await authenticatedFetch(`/api/expense-proof?${params}`);
      if (!response.ok) throw new Error('Failed to fetch expenses');
      const result = await response.json();
      return result.data;
    }
  });

  const { data: summary = {} as ExpenseSummary, isLoading: summaryLoading } = useQuery<ExpenseSummary>({
    queryKey: ['expense-summary'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/expense-proof/summary');
      if (!response.ok) throw new Error('Failed to fetch expense summary');
      const result = await response.json();
      return result.data;
    }
  });

  const { data: categories = [] as ExpenseCategory[] } = useQuery<ExpenseCategory[]>({
    queryKey: ['expense-categories'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/expense-proof/categories');
      if (!response.ok) throw new Error('Failed to fetch expense categories');
      const result = await response.json();
      return result.data;
    }
  });

  const approveExpenseMutation = useMutation({
    mutationFn: async (expenseId: string) => {
      const response = await authenticatedFetch(`/api/expense-proof/${expenseId}/approve`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to approve expense');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-summary'] });
    }
  });

  const rejectExpenseMutation = useMutation({
    mutationFn: async ({ expenseId, reason }: { expenseId: string; reason: string }) => {
      const response = await authenticatedFetch(`/api/expense-proof/${expenseId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      if (!response.ok) throw new Error('Failed to reject expense');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-summary'] });
    }
  });

  const handleCreateExpense = async (formData: FormData) => {
    try {
      const response = await authenticatedFetch('/api/expense-proof', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to create expense');
      
      setShowCreateForm(false);
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-summary'] });
    } catch (error) {
      console.error('Create expense error:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'pending_approval': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'paid': return 'bg-blue-100 text-blue-800';
      case 'reimbursed': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'bg-gray-100 text-gray-800';
      case 'medium': return 'bg-blue-100 text-blue-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'urgent': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (expensesLoading || summaryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (expensesError || !expenses) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Failed to load expenses</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expense & Proof Management</h1>
          <p className="text-gray-600">Track expenses with receipt verification and approval workflows</p>
        </div>
        <Button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          Create Expense
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <Card.Header className="pb-2">
              <Card.Title className="text-sm font-medium text-gray-600">Total Expenses</Card.Title>
            </Card.Header>
            <Card.Content>
              <div className="text-2xl font-bold">
                MWK {summary.totalExpenses.toLocaleString()}
              </div>
            </Card.Content>
          </Card>
          
          <Card>
            <Card.Header className="pb-2">
              <Card.Title className="text-sm font-medium text-gray-600">Transactions</Card.Title>
            </Card.Header>
            <Card.Content>
              <div className="text-2xl font-bold">{summary.totalTransactions}</div>
            </Card.Content>
          </Card>
          
          <Card>
            <Card.Header className="pb-2">
              <Card.Title className="text-sm font-medium text-gray-600">Pending Approval</Card.Title>
            </Card.Header>
            <Card.Content>
              <div className="text-2xl font-bold text-yellow-600">{summary.pendingApproval}</div>
            </Card.Content>
          </Card>
          
          <Card>
            <Card.Header className="pb-2">
              <Card.Title className="text-sm font-medium text-gray-600">Awaiting Payment</Card.Title>
            </Card.Header>
            <Card.Content>
              <div className="text-2xl font-bold text-blue-600">{summary.awaitingPayment}</div>
            </Card.Content>
          </Card>
        </div>
      )}

      {/* Create Expense Form */}
      {showCreateForm && (
        <Card>
          <Card.Header>
            <Card.Title>Create New Expense</Card.Title>
          </Card.Header>
          <Card.Content>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleCreateExpense(formData);
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <Input name="title" required className="mt-1" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount</label>
                  <Input name="amount" type="number" step="0.01" required className="mt-1" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select name="categoryId" required className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
                    <option value="">Select category</option>
                    {categories?.map((cat: any) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                  <select name="paymentMethod" required className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
                    <option value="cash">Cash</option>
                    <option value="bank">Bank</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="card">Card</option>
                    <option value="credit">Credit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Vendor Name</label>
                  <Input name="vendorName" className="mt-1" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Priority</label>
                  <select name="priority" required className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea name="description" rows={3} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Receipts</label>
                <input
                  type="file"
                  name="receipts"
                  multiple
                  accept="image/*,.pdf"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Create Expense</Button>
              </div>
            </form>
          </Card.Content>
        </Card>
      )}

      {/* Filters and Search */}
      <Card>
        <Card.Header>
          <Card.Title>Filters</Card.Title>
        </Card.Header>
        <Card.Content>
          <div className="flex flex-col md:flex-row gap-4">
            <Input
              placeholder="Search expenses..."
              value={searchTerm}
              onChange={(e: any) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="paid">Paid</option>
              <option value="reimbursed">Reimbursed</option>
            </select>
            
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categories?.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
        </Card.Content>
      </Card>

      {/* Expenses List */}
      <Card>
        <Card.Header>
          <Card.Title>Expenses ({expenses.length})</Card.Title>
        </Card.Header>
        <Card.Content>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receipts
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expenses.map((expense: any) => (
                  <tr key={expense.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{expense.title}</div>
                        <div className="text-sm text-gray-500">{expense.vendorName}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        MWK {expense.amount.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(expense.status)}`}>
                        {expense.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(expense.priority)}`}>
                        {expense.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(expense.expenseDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {expense.receiptFiles.length > 0 ? (
                        <span className="text-green-600">{expense.receiptFiles.length} files</span>
                      ) : (
                        <span className="text-red-600">No receipts</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => console.log(expense)}
                        >
                          View
                        </Button>
                        {expense.status === 'pending_approval' && (
                          <>
                            {approveExpenseMutation.isPending ? (
                              <span className="text-gray-400">Approving...</span>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => approveExpenseMutation.mutate(expense.id)}
                              >
                                Approve
                              </Button>
                            )}
                            {rejectExpenseMutation.isPending ? (
                              <span className="text-gray-400">Rejecting...</span>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const reason = prompt('Reason for rejection:');
                                  if (reason) {
                                    rejectExpenseMutation.mutate({ expenseId: expense.id, reason });
                                  }
                                }}
                              >
                                Reject
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {expenses.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">No expenses found</p>
              </div>
            )}
          </div>
        </Card.Content>
      </Card>
    </div>
  );
}
